import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ATELIER, FONTS, ICON, MODES, MODELS, MODE_CONTROLS, MODE_PROMPT_PLACEHOLDER, VIDEO_CAPABILITIES } from '../tokens';
import { useWorkspace } from '../WorkspaceContext';
import { IntentChip } from './IntentChip';
import JSZip from 'jszip';

const extractTextFromDocx = async (file) => {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const zip = await JSZip.loadAsync(arrayBuffer);
    const docXmlFile = zip.file("word/document.xml");
    if (!docXmlFile) return '';
    const xmlText = await docXmlFile.async("text");
    
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlText, "application/xml");
    const paragraphNodes = xmlDoc.getElementsByTagName("w:p");
    
    let paragraphs = [];
    for (let i = 0; i < paragraphNodes.length; i++) {
      const pNode = paragraphNodes[i];
      const textNodes = pNode.getElementsByTagName("w:t");
      let pText = "";
      for (let j = 0; j < textNodes.length; j++) {
        pText += textNodes[j].textContent;
      }
      if (pText.trim()) {
        paragraphs.push(pText);
      }
    }
    return paragraphs.join('\n\n');
  } catch (err) {
    console.error("Failed to extract docx text:", err);
    return '';
  }
};

const loadPdfJs = () => {
  return new Promise((resolve, reject) => {
    if (window.pdfjsLib) {
      resolve(window.pdfjsLib);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.4.120/pdf.min.js';
    script.onload = () => {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.4.120/pdf.worker.min.js';
      resolve(window.pdfjsLib);
    };
    script.onerror = (err) => reject(err);
    document.head.appendChild(script);
  });
};

const extractTextFromPdf = async (file) => {
  try {
    const pdfjsLib = await loadPdfJs();
    const arrayBuffer = await file.arrayBuffer();
    const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
    const pdf = await loadingTask.promise;
    
    let text = "";
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const textContent = await page.getTextContent();
      const pageText = textContent.items.map(item => item.str).join(" ");
      text += pageText + "\n\n";
    }
    return text.trim();
  } catch (err) {
    console.error("Failed to extract PDF text:", err);
    return "";
  }
};

const extractTextFromExcel = async (file) => {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const zip = await JSZip.loadAsync(arrayBuffer);
    const parser = new DOMParser();
    
    // Helper to get nodes by local name (namespace agnostic)
    const getNodes = (parent, name) => {
      const results = [];
      const children = parent.childNodes;
      for (let i = 0; i < children.length; i++) {
        if (children[i].nodeType === 1 && (children[i].localName === name || children[i].tagName === name)) {
          results.push(children[i]);
        }
      }
      return results;
    };

    const getFirstNodeText = (parent, name) => {
      const nodes = getNodes(parent, name);
      return nodes.length > 0 ? nodes[0].textContent : null;
    };

    // 1. Get shared strings
    const sharedStringsFile = zip.file("xl/sharedStrings.xml");
    let sharedStrings = [];
    if (sharedStringsFile) {
      const ssText = await sharedStringsFile.async("text");
      const xmlDoc = parser.parseFromString(ssText, "application/xml");
      const tNodes = xmlDoc.getElementsByTagName("t");
      for (let i = 0; i < tNodes.length; i++) {
        sharedStrings.push(tNodes[i].textContent);
      }
    }

    // 2. Get sheet names
    const workbookFile = zip.file("xl/workbook.xml");
    let sheetNames = [];
    if (workbookFile) {
      const wbText = await workbookFile.async("text");
      const wbDoc = parser.parseFromString(wbText, "application/xml");
      const sheetNodes = wbDoc.getElementsByTagName("sheet");
      for (let i = 0; i < sheetNodes.length; i++) {
        sheetNames.push(sheetNodes[i].getAttribute("name") || `Sheet ${i + 1}`);
      }
    }

    // 3. Extract sheets
    let allContent = [];
    for (let i = 0; i < (sheetNames.length || 1); i++) {
      const sheetFile = zip.file(`xl/worksheets/sheet${i + 1}.xml`);
      if (!sheetFile) continue;

      const sheetText = await sheetFile.async("text");
      const xmlDoc = parser.parseFromString(sheetText, "application/xml");
      // Use localName or tagName for cross-browser/namespace compatibility
      const sheetData = getNodes(xmlDoc.documentElement, "sheetData")[0];
      if (!sheetData) continue;

      const rowNodes = getNodes(sheetData, "row");
      let rows = [];

      for (let j = 0; j < rowNodes.length; j++) {
        const cNodes = getNodes(rowNodes[j], "c");
        let rowData = [];
        for (let k = 0; k < cNodes.length; k++) {
          const cNode = cNodes[k];
          const type = cNode.getAttribute("t");
          let val = "";

          if (type === "s") {
            const v = getFirstNodeText(cNode, "v");
            if (v !== null) val = sharedStrings[parseInt(v)] || "";
          } else if (type === "inlineStr") {
            const isNode = getNodes(cNode, "is")[0];
            if (isNode) val = getFirstNodeText(isNode, "t") || "";
          } else {
            val = getFirstNodeText(cNode, "v") || "";
          }
          rowData.push(val);
        }
        if (rowData.some(v => v && v.trim())) {
          rows.push(rowData.join("\t"));
        }
      }

      if (rows.length > 0) {
        allContent.push(`[SHEET: ${sheetNames[i] || `Sheet ${i + 1}`}]\n${rows.join("\n")}`);
      }
    }
    
    return allContent.join("\n\n");
  } catch (err) {
    console.error("Failed to extract Excel text:", err);
    return "";
  }
};


/**
 * SmartComposer - the unified prompt at the bottom of the thread.
 *
 *   ┌── Aurora ribbon (1px breathing gradient) ─────────────────┐
 *   │                                                            │
 *   │   ┌── Card ───────────────────────────────────────────┐    │
 *   │   │  textarea                                          │    │
 *   │   │  ───────────────────────────────────────────       │    │
 *   │   │  [✨ IntentChip] [model] [aspect] [...]   [↵ send] │    │
 *   │   └────────────────────────────────────────────────────┘    │
 *   │   "DAGGPT can make mistakes. Outputs anchored on DAGCHAIN."│
 *   └────────────────────────────────────────────────────────────┘
 */
export function SmartComposer({ embedded = false } = {}) {
  const {
    activeMode, modes, switchMode, updateMode, parseSlashCommand,
    draftPrompt, setDraftPrompt, sendTurn, availableModels, defaultModels, conversation,
    newConversation, attachments, setAttachments, stopGeneration
  } = useWorkspace();
  const current = modes[activeMode];
  const isGenerating = conversation?.turns?.some(t => t.status === 'pending');

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const lastTurn = conversation?.turns?.[conversation.turns.length - 1];
  let suggestions = lastTurn?.role === 'assistant' && lastTurn.status === 'done' ? lastTurn.meta?.suggestions : null;

  // Fallback suggestions for historically loaded threads or truncated/missing responses
  if (lastTurn?.role === 'assistant' && lastTurn.status === 'done' && (!suggestions || !Array.isArray(suggestions) || suggestions.length === 0)) {
    if (activeMode === 'chat') {
      suggestions = [
        "Can you go deeper into the first module?",
        "What are some real-world industry use cases?",
        "Could you give me a hands-on exercise to practice?"
      ];
    } else if (activeMode === 'image') {
      suggestions = [
        "Make it more cinematic and dramatic",
        "Try a different art style for this",
        "Describe the visual details in depth"
      ];
    } else if (activeMode === 'video') {
      suggestions = [
        "Increase the duration to 8s",
        "Try cinematic lighting",
        "Make the movement faster and smoother"
      ];
    } else {
      suggestions = [
        "Could you refine this generation?",
        "Show me alternative options",
        "Explain how I can optimize the settings"
      ];
    }
  }

  const textareaRef = useRef(null);
  const cardRef = useRef(null);
  const fileRef = useRef(null);
  const [focused, setFocused] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [slashOpen, setSlashOpen] = useState(false);
  const [slashFilter, setSlashFilter] = useState('');
  const [slashIndex, setSlashIndex] = useState(0);
  const [mobileDrawer, setMobileDrawer] = useState(null); // null | 'model' | 'settings'

  const handleFileClick = () => fileRef.current?.click();

  const uploadFileDirectly = async (file) => {
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const apiUrl = 'https://api.daggpt.network';
      const res = await fetch(`${apiUrl}/api/upload`, {
        method: 'POST',
        headers: {
          'x-user-id': localStorage.getItem('daggpt:user_id') || 'guest_user'
        },
        body: formData,
      });
      const data = await res.json();
      if (data.url) {
        // Extract text if it's a text-based file or word doc
        let extractedText = '';
        const textTypes = ['text/plain', 'text/csv', 'application/json', 'text/markdown'];
        if (textTypes.includes(file.type) || file.name.match(/\.(txt|csv|json|md)$/i)) {
          try {
            extractedText = await file.text();
          } catch (te) { console.warn('Text extraction failed:', te); }
        } else if (file.name.match(/\.docx$/i)) {
          try {
            extractedText = await extractTextFromDocx(file);
          } catch (te) { console.warn('Docx text extraction failed:', te); }
        } else if (file.name.match(/\.pdf$/i)) {
          try {
            extractedText = await extractTextFromPdf(file);
          } catch (te) { console.warn('PDF text extraction failed:', te); }
        } else if (file.name.match(/\.xlsx$/i)) {
          try {
            extractedText = await extractTextFromExcel(file);
          } catch (te) { console.warn('Excel text extraction failed:', te); }
        }


        // Truncate if too large (e.g. 100kb limit for direct prompt injection)
        if (extractedText && extractedText.length > 100000) {
          extractedText = extractedText.slice(0, 100000) + '\n... [Truncated due to size]';
        }

        setAttachments(prev => [...prev, { 
          id: Math.random().toString(36).slice(2), 
          url: data.url, 
          type: file.type, 
          name: file.name,
          extractedText // Store the text content
        }]);
      }
    } catch (err) {
      console.error('Upload failed:', err);
    } finally {
      setUploading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await uploadFileDirectly(file);
    e.target.value = ''; // Reset input
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      for (let i = 0; i < e.dataTransfer.files.length; i++) {
        await uploadFileDirectly(e.dataTransfer.files[i]);
      }
    }
  };

  const removeAttachment = (id) => {
    setAttachments(prev => prev.filter(a => a.id !== id));
  };

  // Close menus on outside click / Esc
  useEffect(() => {
    if (!openMenu) return;
    const onClick = (e) => { if (cardRef.current && !cardRef.current.contains(e.target)) setOpenMenu(null); };
    const onEsc = (e) => { if (e.key === 'Escape') setOpenMenu(null); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [openMenu]);

  // Slash command detection
  const detectSlash = useCallback((val) => {
    const m = val.match(/^\/([a-z]*)$/i);
    if (m) { setSlashOpen(true); setSlashFilter(m[1].toLowerCase()); setSlashIndex(0); }
    else   { setSlashOpen(false); }
  }, []);
  const slashMatches = MODES.filter(m => m.id !== activeMode && m.slashCmd.startsWith(slashFilter));
  const applyModeSwitch = useCallback((newMode) => {
    const parsed = parseSlashCommand(draftPrompt);
    switchMode(newMode);
    if (parsed) setDraftPrompt(parsed.rest || '');
    setSlashOpen(false);
    setOpenMenu(null);
    setTimeout(() => textareaRef.current?.focus(), 50);
  }, [draftPrompt, parseSlashCommand, switchMode, setDraftPrompt]);

  // Input handlers
  const autoResize = (el) => {
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 240) + 'px';
  };
  const handleChange = (e) => {
    setDraftPrompt(e.target.value);
    detectSlash(e.target.value);
    autoResize(e.target);
  };
  const handleKeyDown = (e) => {
    if (slashOpen) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setSlashIndex(i => (i + 1) % Math.max(slashMatches.length, 1)); return; }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setSlashIndex(i => (i - 1 + slashMatches.length) % Math.max(slashMatches.length, 1)); return; }
      if ((e.key === 'Enter' || e.key === 'Tab') && slashMatches[slashIndex]) {
        e.preventDefault(); applyModeSwitch(slashMatches[slashIndex].id); return;
      }
      if (e.key === 'Escape') { e.preventDefault(); setSlashOpen(false); return; }
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      const parsed = parseSlashCommand(draftPrompt);
      if (parsed && parsed.mode !== activeMode) { e.preventDefault(); applyModeSwitch(parsed.mode); return; }
      if (draftPrompt.trim()) { e.preventDefault(); handleSubmit(); }
    }
  };
  const handleSubmit = () => {
    if (!draftPrompt.trim()) return;

    sendTurn();
    requestAnimationFrame(() => {
      if (textareaRef.current) textareaRef.current.style.height = 'auto';
    });
  };

  const placeholder = MODE_PROMPT_PLACEHOLDER[activeMode] || MODE_PROMPT_PLACEHOLDER.chat;
  const canSubmit = draftPrompt.trim().length > 0;
  const modelList = (availableModels && availableModels[activeMode]?.length)
    ? availableModels[activeMode]
    : (MODELS[activeMode] || []);
  let activeModel = modelList.find(mm => mm.id === current.model);
  if (current.model === 'openrouter/auto') {
    activeModel = {
      id: 'openrouter/auto',
      label: 'Multi Agent',
      provider: 'OpenRouter',
    };
  } else if (!activeModel) {
    activeModel = modelList[0];
  }
  
  // Dynamic controls logic - Filter options based on model capabilities
  let controls = MODE_CONTROLS[activeMode] || [];
  if (activeMode === 'chat') {
    controls = [];
  }
  if (activeMode === 'video' && VIDEO_CAPABILITIES[activeModel?.id]) {
    const caps = VIDEO_CAPABILITIES[activeModel.id];
    controls = controls.map(ctl => {
      if (ctl.key === 'duration' && caps.durations) {
        return { ...ctl, options: caps.durations.map(d => ({ id: d, label: `${d}s` })) };
      }
      if (ctl.key === 'aspect' && caps.aspects) {
        return { ...ctl, options: caps.aspects.map(a => ({ id: a, label: a })) };
      }
      if (ctl.key === 'resolution' && caps.resolutions) {
        return { ...ctl, options: caps.resolutions.map(r => ({ id: r, label: r })) };
      }
      if (ctl.key === 'mode' && caps.modes) {
        return { ...ctl, options: caps.modes.map(m => ({ id: m, label: m.charAt(0).toUpperCase() + m.slice(1) })) };
      }
      return ctl;
    });
  }

  const displayModelLabel = (() => {
    if (!activeModel?.label) return 'Model';
    if (!isMobile) return activeModel.label;
    const label = activeModel.label;
    if (label.toLowerCase().includes('gemini')) return 'Gemini';
    if (label.toLowerCase().includes('grok')) return 'Grok';
    if (label.toLowerCase().includes('claude')) return 'Claude';
    if (label.toLowerCase().includes('deepseek')) return 'DeepSeek';
    if (label.toLowerCase().includes('llama')) return 'Llama';
    if (label.toLowerCase().includes('veo')) return 'Veo';
    if (label.toLowerCase().includes('lyria')) return 'Lyria';
    if (label.toLowerCase().includes('imagen')) return 'Imagen';
    return label.split(' ')[0] || 'Model';
  })();

  return (
    <div style={{
      flexShrink: 0,
      padding: embedded ? 0 : (isMobile ? '10px 10px 12px' : '14px 24px 18px'),
      background: embedded ? 'transparent' : ATELIER.bg,
      borderTop: embedded ? 'none' : `1px solid ${ATELIER.borderSoft}`,
      position: 'relative',
    }}>
      {/* Aurora ribbon - the AI heartbeat (1px breathing gradient) */}
      {!embedded && (
        <div aria-hidden style={{
          position: 'absolute',
          top: 0, left: 24, right: 24,
          height: 1,
          overflow: 'hidden',
        }}>
          <div className="dgw-aurora" style={{
            width: '100%', height: '100%',
          }} />
        </div>
      )}

      <div
        ref={cardRef}
        style={{
          maxWidth: embedded ? '100%' : 1024,
          margin: '0 auto',
          position: 'relative',
        }}
      >
        {/* Suggested Follow-ups */}
        <AnimatePresence>
          {suggestions && suggestions.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              style={{
                display: 'flex', gap: 8, paddingBottom: 12, overflowX: 'auto',
                scrollbarWidth: 'none', msOverflowStyle: 'none'
              }}
              className="dgw-scroll"
            >
              {suggestions.map((sug, i) => (
                <button
                  key={i}
                  onClick={() => { setDraftPrompt(sug); setTimeout(handleSubmit, 50); }}
                  style={{
                    flexShrink: 0,
                    background: ATELIER.bgRaised,
                    border: `1px solid ${ATELIER.borderSoft}`,
                    borderRadius: 16,
                    padding: '8px 14px',
                    fontSize: 12.5,
                    color: ATELIER.ink,
                    fontFamily: FONTS.sans,
                    cursor: 'pointer',
                    boxShadow: ATELIER.shadowS,
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = ATELIER.accent; e.currentTarget.style.color = ATELIER.accent; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = ATELIER.borderSoft; e.currentTarget.style.color = ATELIER.ink; }}
                >
                  {sug}
                </button>
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        <div style={{
          background: embedded ? 'transparent' : ATELIER.bgRaised,
          border: embedded ? 'none' : `1px solid ${dragActive ? ATELIER.accent : (focused ? ATELIER.borderAccent : ATELIER.border)}`,
          borderRadius: embedded ? 0 : 16,
          boxShadow: embedded ? 'none' : ((focused || dragActive) ? ATELIER.shadowFocus + ', ' + ATELIER.shadowL : ATELIER.shadowM),
          transition: 'border-color 0.2s, box-shadow 0.2s',
          position: 'relative',
        }}
        onDragEnter={handleDrag}
        onDragOver={handleDrag}
        onDragLeave={handleDrag}
        onDrop={handleDrop}
        >
          <AnimatePresence>
            {dragActive && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'rgba(255, 255, 255, 0.93)',
                  backdropFilter: 'blur(8px)',
                  borderRadius: 16,
                  zIndex: 100,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  border: `2px dashed ${ATELIER.accent}`,
                  pointerEvents: 'none',
                }}
              >
                <div style={{
                  width: 44, height: 44, borderRadius: '50%',
                  background: ATELIER.accentSoft,
                  color: ATELIER.accent,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: `0 4px 14px rgba(124, 58, 237, 0.15)`
                }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" y1="3" x2="12" y2="15" />
                  </svg>
                </div>
                <span style={{
                  fontFamily: FONTS.sans,
                  fontSize: 13.5,
                  fontWeight: 600,
                  color: ATELIER.ink,
                }}>Drop files to attach to thread</span>
                <span style={{
                  fontFamily: FONTS.sans,
                  fontSize: 11,
                  color: ATELIER.inkMuted,
                }}>Supports docs, sheets, PDFs, slides, images, videos</span>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Textarea */}
          <div style={{ padding: '14px 18px 4px' }}>
          <textarea
            ref={textareaRef}
            rows={1}
            value={draftPrompt}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onFocus={() => setFocused(true)}
            onBlur={() => { setFocused(false); setTimeout(() => setSlashOpen(false), 120); }}
            placeholder={placeholder}
            style={{
              width: '100%',
              resize: 'none',
              border: 'none',
              outline: 'none',
              padding: 0,
              fontFamily: FONTS.sans,
              fontSize: 15.5,
              lineHeight: 1.55,
              color: ATELIER.ink,
              background: 'transparent',
              caretColor: ATELIER.accent,
              minHeight: 24,
              maxHeight: 240,
              display: 'block',
              letterSpacing: '-0.005em',
            }}
          />
        </div>
        
        {/* Attachments Preview */}
        <AnimatePresence>
          {attachments.length > 0 && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              style={{
                display: 'flex', gap: 10, padding: '0 18px 12px',
                overflowX: 'auto', flexWrap: 'wrap'
              }}
            >
              {attachments.map(a => (
                <motion.div
                  key={a.id}
                  layout
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  style={{
                    width: 56, height: 56, borderRadius: 10,
                    overflow: 'hidden', position: 'relative',
                    border: `1px solid ${ATELIER.border}`,
                    background: ATELIER.bgHover,
                    flexShrink: 0,
                    boxShadow: ATELIER.shadowS
                  }}
                >
                  { (a.type?.startsWith('image/') || a.url?.match(/\.(jpg|jpeg|png|webp|gif)$/i)) ? (
                    <img src={a.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <div style={{ 
                      width: '100%', height: '100%', 
                      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                      background: ATELIER.bgHover, gap: 4, padding: 4
                    }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={ATELIER.inkMid} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                      </svg>
                      <span style={{ fontSize: 9, color: ATELIER.inkSoft, fontWeight: 700, textTransform: 'uppercase' }}>
                        {a.name?.split('.').pop() || 'FILE'}
                      </span>
                    </div>
                  )}
                  <button
                    onClick={() => removeAttachment(a.id)}
                    style={{
                      position: 'absolute', top: 2, right: 2,
                      width: 16, height: 16, borderRadius: '50%',
                      background: 'rgba(0,0,0,0.5)', color: '#fff',
                      border: 'none', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 10
                    }}
                  >
                    ✕
                  </button>
                </motion.div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Slash autocomplete */}
        <AnimatePresence>
          {slashOpen && slashMatches.length > 0 && !openMenu && (
            <motion.div
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 4 }}
              transition={{ duration: 0.14 }}
              style={{
                position: 'absolute',
                bottom: 'calc(100% + 6px)',
                left: 0, right: 0,
                margin: '0 auto', maxWidth: 280,
                background: ATELIER.bg,
                border: `1px solid ${ATELIER.borderStrong}`,
                borderRadius: 12,
                padding: 5,
                boxShadow: ATELIER.shadowL,
                zIndex: 30,
              }}
            >
              <div style={{
                fontFamily: FONTS.mono, fontSize: 9.5, letterSpacing: '0.22em',
                color: ATELIER.inkMuted, fontWeight: 700,
                padding: '8px 10px 6px', textTransform: 'uppercase',
              }}>Switch mode</div>
              {slashMatches.map((m, i) => {
                const tint = ATELIER.modes[m.id]?.tint || ATELIER.accentSoft;
                const accent = ATELIER.modes[m.id]?.accent || ATELIER.accent;
                return (
                  <button
                    key={m.id}
                    onMouseEnter={() => setSlashIndex(i)}
                    onClick={() => applyModeSwitch(m.id)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 9,
                      width: '100%', padding: '8px 10px',
                      background: slashIndex === i ? tint : 'transparent',
                      border: 'none', borderRadius: 8,
                      cursor: 'pointer', fontSize: 12.5,
                      color: ATELIER.ink, fontFamily: FONTS.sans,
                      textAlign: 'left',
                    }}
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                      stroke={accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d={ICON[m.id]} />
                    </svg>
                    <span style={{ fontFamily: FONTS.mono, fontSize: 11, color: accent, fontWeight: 600, minWidth: 50 }}>
                      /{m.slashCmd}
                    </span>
                    <span style={{ color: ATELIER.inkSoft }}>{m.label}</span>
                  </button>
                );
              })}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Bottom row */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '8px 12px 10px 14px',
          borderTop: `1px solid ${ATELIER.borderSoft}`,
          position: 'relative',
        }}>
          {/* Controls Area */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              flex: 1,
              marginRight: 8,
            }}
          >
            <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
              <IntentChip />
            </div>

            {/* Divider */}
            <span style={{
              width: 1, height: 18, background: ATELIER.border,
              margin: '0 4px',
              flexShrink: 0,
            }} />

            {/* Model picker */}
            <div style={{ flexShrink: 0 }}>
              <Pill
                open={!isMobile && openMenu === 'model'}
                onToggle={() => {
                  if (isMobile) {
                    setMobileDrawer('model');
                  } else {
                    setOpenMenu(v => v === 'model' ? null : 'model');
                  }
                }}
                label={displayModelLabel}
                icon={ICON.sparkle}
                align="right"
              >
                {!isMobile ? (
                  <ModelMenu 
                     modelList={modelList}
                     activeModel={activeModel}
                     defaultModels={defaultModels}
                     activeMode={activeMode}
                     webSearch={current.webSearch}
                     onToggleWebSearch={(enabled) => updateMode(activeMode, { webSearch: enabled })}
                     onSelect={(mId) => {
                       const updates = { model: mId };
                       
                       // Auto-fix duration/aspect if model capabilities changed
                       if (activeMode === 'video' && VIDEO_CAPABILITIES[mId]) {
                         const caps = VIDEO_CAPABILITIES[mId];
                         if (caps.durations && !caps.durations.includes(current.duration)) {
                           updates.duration = caps.durations[0];
                         }
                         if (caps.aspects && !caps.aspects.includes(current.aspect)) {
                           updates.aspect = caps.aspects[0];
                         }
                         if (caps.resolutions && !caps.resolutions.includes(current.resolution)) {
                           updates.resolution = caps.resolutions[0];
                         }
                         if (caps.modes && !caps.modes.includes(current.mode)) {
                           updates.mode = caps.modes[0];
                         }
                       }

                       updateMode(activeMode, updates);
                       setOpenMenu(null);
                       setTimeout(() => textareaRef.current?.focus(), 40);
                     }}
                  />
                ) : null}
              </Pill>
            </div>

            {/* Per-mode controls (liquid morph on mode change) - Desktop Only */}
            {!isMobile && (
              <AnimatePresence mode="wait">
                <motion.div
                  key={`controls-${activeMode}`}
                  initial={{ opacity: 0, y: 4, filter: 'blur(4px)' }}
                  animate={{ opacity: 1, y: 0, filter: 'blur(0)' }}
                  exit={{ opacity: 0, y: -4, filter: 'blur(4px)' }}
                  transition={{ duration: 0.18 }}
                  style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'nowrap', flexShrink: 0 }}
                >
                  {controls.map(ctl => {
                    const key = `ctl:${ctl.key}`;
                    const raw = current[ctl.key];
                    const selected = ctl.options.find(o => o.id === raw) || ctl.options[0];
                    const display = ctl.format ? ctl.format(selected?.id) : (selected?.label || selected?.id);
                    return (
                      <Pill
                        key={ctl.key}
                        open={openMenu === key}
                        onToggle={() => setOpenMenu(v => v === key ? null : key)}
                        label={display}
                        icon={ICON[ctl.icon] || ICON.cog}
                      >
                        <Menu>
                          <MenuHeader>{ctl.label}</MenuHeader>
                          {ctl.options.map(opt => (
                            <MenuItem
                              key={String(opt.id)}
                              active={opt.id === selected?.id}
                              onClick={() => {
                                updateMode(activeMode, { [ctl.key]: opt.id });
                                setOpenMenu(null);
                                setTimeout(() => textareaRef.current?.focus(), 40);
                              }}
                              primary={opt.label}
                              compact
                            />
                          ))}
                        </Menu>
                      </Pill>
                    );
                  })}
                </motion.div>
              </AnimatePresence>
            )}

            {/* Mobile Settings Drawer Trigger (Only shown if mode has controls like Video or Image) */}
            {isMobile && controls.length > 0 && (
              <div style={{ flexShrink: 0 }}>
                <IconBtn
                  title="Generation Settings"
                  onClick={() => setMobileDrawer('settings')}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                    stroke={mobileDrawer === 'settings' ? ATELIER.accent : "currentColor"} 
                    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="3"></circle>
                    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
                  </svg>
                </IconBtn>
              </div>
            )}

            {/* Attach */}
            <input 
              type="file" 
              ref={fileRef} 
              onChange={handleFileUpload} 
              accept="image/*,.pdf,.txt,.doc,.docx,.xls,.xlsx,.csv" 
              style={{ display: 'none' }} 
            />
            <div style={{ flexShrink: 0 }}>
              <IconBtn 
                title="Attach file" 
                onClick={handleFileClick} 
                disabled={uploading}
              >
                {uploading ? (
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                    style={{ width: 14, height: 14, border: `2px solid ${ATELIER.accent}`, borderTopColor: 'transparent', borderRadius: '50%' }}
                  />
                ) : (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d={ICON.attach} />
                  </svg>
                )}
              </IconBtn>
            </div>
          </div>

          {/* Sticky Send / Stop Button on the Right */}
          <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
            {isGenerating ? (
              <StopButton onClick={stopGeneration} />
            ) : (
              <SendButton canSubmit={canSubmit} onClick={handleSubmit} modelLabel={activeModel?.label} />
            )}
          </div>
        </div>

        {/* Mobile Settings Drawer Bottom Sheet */}
        <AnimatePresence>
          {isMobile && mobileDrawer === 'settings' && (
            <>
              {/* Backdrop overlay */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.4 }}
                exit={{ opacity: 0 }}
                onClick={() => setMobileDrawer(null)}
                style={{
                  position: 'fixed',
                  inset: 0,
                  background: '#0a0a14',
                  zIndex: 9999,
                }}
              />
              {/* Sheet content */}
              <motion.div
                initial={{ y: '30%', opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: '30%', opacity: 0 }}
                transition={{ type: 'spring', damping: 25, stiffness: 220 }}
                style={{
                  position: 'fixed',
                  bottom: 24, left: 16, right: 16,
                  background: '#fff',
                  borderRadius: 24,
                  padding: '24px 20px 24px',
                  zIndex: 10000,
                  boxShadow: '0 20px 50px rgba(10,10,25,0.15), 0 0 0 1px rgba(0,0,0,0.05)',
                  maxHeight: '80vh',
                  overflowY: 'auto',
                }}
              >
                
                <h3 style={{
                  fontSize: 18, fontWeight: 700,
                  fontFamily: FONTS.sans, color: ATELIER.ink,
                  margin: '0 0 20px', textAlign: 'center',
                  letterSpacing: '-0.01em',
                }}>
                  Generation Settings
                </h3>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                  {controls.map(ctl => {
                    const raw = current[ctl.key];
                    const selected = ctl.options.find(o => o.id === raw) || ctl.options[0];
                    return (
                      <div key={ctl.key} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <label style={{
                          fontSize: 13, fontWeight: 600,
                          color: ATELIER.inkSoft, textTransform: 'uppercase',
                          letterSpacing: '0.04em',
                        }}>
                          {ctl.label}
                        </label>
                        <div style={{
                          display: 'flex', flexWrap: 'wrap', gap: 8,
                        }}>
                          {ctl.options.map(opt => (
                            <button
                              key={String(opt.id)}
                              onClick={() => updateMode(activeMode, { [ctl.key]: opt.id })}
                              style={{
                                padding: '8px 14px',
                                borderRadius: 10,
                                fontFamily: FONTS.sans,
                                fontSize: 13,
                                fontWeight: 500,
                                cursor: 'pointer',
                                border: `1.5px solid ${opt.id === selected?.id ? ATELIER.accent : ATELIER.border}`,
                                background: opt.id === selected?.id ? ATELIER.accentSoft : 'transparent',
                                color: opt.id === selected?.id ? ATELIER.accent : ATELIER.inkMid,
                                transition: 'all 0.15s ease',
                              }}
                            >
                              {opt.label || opt.id}
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                <button
                  onClick={() => setMobileDrawer(null)}
                  style={{
                    width: '100%',
                    marginTop: 30,
                    padding: '12px',
                    borderRadius: 12,
                    background: ATELIER.bgInk,
                    color: '#fff',
                    border: 'none',
                    fontFamily: FONTS.sans,
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Close Settings
                </button>
              </motion.div>
            </>
          )}
        </AnimatePresence>

        {/* Mobile Model Selection Drawer Bottom Sheet */}
        <AnimatePresence>
          {isMobile && mobileDrawer === 'model' && (
            <>
              {/* Backdrop overlay */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.4 }}
                exit={{ opacity: 0 }}
                onClick={() => setMobileDrawer(null)}
                style={{
                  position: 'fixed',
                  inset: 0,
                  background: '#0a0a14',
                  zIndex: 9999,
                }}
              />
              {/* Sheet content */}
              <motion.div
                initial={{ y: '30%', opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: '30%', opacity: 0 }}
                transition={{ type: 'spring', damping: 25, stiffness: 220 }}
                style={{
                  position: 'fixed',
                  bottom: 24, left: 16, right: 16,
                  background: '#fff',
                  borderRadius: 24,
                  padding: '24px 20px 24px',
                  zIndex: 10000,
                  boxShadow: '0 20px 50px rgba(10,10,25,0.15), 0 0 0 1px rgba(0,0,0,0.05)',
                  maxHeight: '75vh',
                  overflowY: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                
                <h3 style={{
                  fontSize: 18, fontWeight: 700,
                  fontFamily: FONTS.sans, color: ATELIER.ink,
                  margin: '0 0 16px', textAlign: 'center',
                  letterSpacing: '-0.01em',
                  flexShrink: 0,
                }}>
                  Select AI Model
                </h3>
                
                {/* Nested ModelMenu */}
                <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                  <ModelMenu 
                     mobile
                     modelList={modelList}
                     activeModel={activeModel}
                     defaultModels={defaultModels}
                     activeMode={activeMode}
                     webSearch={current.webSearch}
                     onToggleWebSearch={(enabled) => updateMode(activeMode, { webSearch: enabled })}
                     onSelect={(mId) => {
                       const updates = { model: mId };
                       
                       // Auto-fix duration/aspect if model capabilities changed
                       if (activeMode === 'video' && VIDEO_CAPABILITIES[mId]) {
                         const caps = VIDEO_CAPABILITIES[mId];
                         if (caps.durations && !caps.durations.includes(current.duration)) {
                           updates.duration = caps.durations[0];
                         }
                         if (caps.aspects && !caps.aspects.includes(current.aspect)) {
                           updates.aspect = caps.aspects[0];
                         }
                         if (caps.resolutions && !caps.resolutions.includes(current.resolution)) {
                           updates.resolution = caps.resolutions[0];
                         }
                         if (caps.modes && !caps.modes.includes(current.mode)) {
                           updates.mode = caps.modes[0];
                         }
                       }

                       updateMode(activeMode, updates);
                       setMobileDrawer(null);
                       setTimeout(() => textareaRef.current?.focus(), 40);
                     }}
                  />
                </div>
                
                <button
                  onClick={() => setMobileDrawer(null)}
                  style={{
                    width: '100%',
                    marginTop: 20,
                    padding: '12px',
                    borderRadius: 12,
                    background: ATELIER.bgInk,
                    color: '#fff',
                    border: 'none',
                    fontFamily: FONTS.sans,
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: 'pointer',
                    flexShrink: 0,
                  }}
                >
                  Close Models
                </button>
              </motion.div>
            </>
          )}
        </AnimatePresence>
        </div>
      </div>

      {/* Tiny hint below - only in non-embedded mode */}
      {!embedded && (
        <div style={{
          textAlign: 'center', marginTop: 10,
          fontSize: 10.5, color: ATELIER.inkMuted,
          fontFamily: FONTS.sans, letterSpacing: '-0.005em',
        }}>
          DAGGPT can make mistakes. Outputs are anchored on DAGCHAIN for provenance.
        </div>
      )}
    </div>
  );
}

/* ── Pill / Menu primitives (light-theme) ──────────────────── */
function Pill({ open, onToggle, label, icon, align = 'left', children }) {
  const [hover, setHover] = useState(false);
  return (
    <div style={{ position: 'relative', flexShrink: 0 }}>
      <button
        type="button"
        onClick={onToggle}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          height: 30, padding: '0 10px',
          background: open ? ATELIER.bgHover : (hover ? ATELIER.bgHover : 'transparent'),
          border: `1px solid ${open ? ATELIER.borderStrong : 'transparent'}`,
          borderRadius: 8,
          cursor: 'pointer',
          fontFamily: 'inherit', fontSize: 12, fontWeight: 500,
          color: ATELIER.inkMid,
          letterSpacing: '-0.005em',
          whiteSpace: 'nowrap',
          transition: 'background 0.12s, border-color 0.12s',
        }}
      >
        {icon && (
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
            stroke={ATELIER.inkSoft} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d={icon} />
          </svg>
        )}
        <span>{label}</span>
        <motion.svg
          width="9" height="9" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          style={{ opacity: 0.5 }}
        >
          <path d={ICON.chevronDown} />
        </motion.svg>
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 6, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 6, scale: 0.97 }}
            transition={{ duration: 0.18 }}
            style={{
              position: 'absolute',
              bottom: 'calc(100% + 8px)',
              left: align === 'right' ? 'auto' : 0,
              right: align === 'right' ? 0 : 'auto',
              zIndex: 30,
              minWidth: 240,
              transformOrigin: align === 'right' ? 'bottom right' : 'bottom left',
            }}
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function Menu({ children }) {
  return (
    <div style={{
      background: ATELIER.bg,
      border: `1px solid ${ATELIER.borderStrong}`,
      borderRadius: 12,
      padding: 5,
      boxShadow: ATELIER.shadowL,
    }}>{children}</div>
  );
}

function MenuHeader({ children }) {
  return (
    <div style={{
      fontFamily: FONTS.mono, fontSize: 9.5, letterSpacing: '0.22em',
      fontWeight: 700, padding: '8px 10px 6px',
      color: ATELIER.inkMuted, textTransform: 'uppercase',
    }}>{children}</div>
  );
}

function MenuItem({ active, compact, primary, secondary, badge, onClick }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 8,
        width: '100%', padding: compact ? '7px 10px' : '8px 10px',
        background: active ? ATELIER.accentSoft : (hover ? ATELIER.bgHover : 'transparent'),
        border: 'none', borderRadius: 8,
        cursor: 'pointer',
        fontFamily: 'inherit', color: ATELIER.ink, textAlign: 'left',
        transition: 'background 0.12s',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 12.5, fontWeight: 500, lineHeight: 1.3, letterSpacing: '-0.005em',
          color: active ? ATELIER.accent : ATELIER.ink,
        }}>{primary}</div>
        {secondary && (
          <div style={{ fontSize: 10.5, color: ATELIER.inkSoft, marginTop: 1 }}>
            {secondary}
          </div>
        )}
      </div>
      {badge && (
        <span style={{
          fontFamily: FONTS.mono, fontSize: 8.5, letterSpacing: '0.16em',
          color: ATELIER.inkSoft, fontWeight: 700,
          padding: '2px 5px', borderRadius: 4,
          background: ATELIER.bgHover,
          border: `1px solid ${ATELIER.border}`,
          textTransform: 'uppercase',
        }}>{badge}</span>
      )}
      {active && (
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
          stroke={ATELIER.accent} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d={ICON.check} />
        </svg>
      )}
    </button>
  );
}

function IconBtn({ title, children, onClick }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        width: 30, height: 30, borderRadius: 8,
        background: hover ? ATELIER.bgHover : 'transparent',
        border: 'none', cursor: 'pointer',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        color: hover ? ATELIER.inkMid : ATELIER.inkSoft,
        transition: 'background 0.12s, color 0.12s',
        flexShrink: 0,
      }}
    >
      {children}
    </button>
  );
}

/**
 * SendButton - dark-ink pill (rare full-bleed dark element on the white shell).
 * Quiet luxury vs. the everyone-does-violet-gradient look. Reads as the
 * decisive primary action without screaming.
 *
 *   ┌─────────────────┐    disabled:   ┌─────────────┐
 *   │  Send  ↵        │   ────────►    │  ↵          │
 *   └─────────────────┘                └─────────────┘
 */
function SendButton({ canSubmit, onClick, modelLabel }) {
  const [hover, setHover] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (!canSubmit) {
    if (isMobile) {
      return (
        <div
          aria-disabled
          style={{
            flexShrink: 0,
            width: 32, height: 32,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            background: ATELIER.bgHover,
            border: `1px solid ${ATELIER.border}`,
            borderRadius: '50%',
            color: ATELIER.inkMuted,
            cursor: 'not-allowed',
          }}
        >
          <span style={{ fontSize: 16, fontWeight: 700 }}>↑</span>
        </div>
      );
    }

    // Disabled - collapsed mono kbd, doesn't shout (Desktop)
    return (
      <div
        aria-disabled
        style={{
          flexShrink: 0,
          height: 32,
          padding: '0 12px',
          display: 'inline-flex', alignItems: 'center', gap: 6,
          background: ATELIER.bgHover,
          border: `1px solid ${ATELIER.border}`,
          borderRadius: 8,
          color: ATELIER.inkMuted,
          fontFamily: FONTS.mono, fontSize: 11, letterSpacing: '0.04em',
          fontWeight: 600,
          cursor: 'not-allowed',
        }}
      >
        <span style={{ opacity: 0.7 }}>↵</span>
        <span style={{ opacity: 0.6 }}>send</span>
      </div>
    );
  }

  if (isMobile) {
    return (
      <motion.button
        type="button"
        onClick={onClick}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        whileTap={{ scale: 0.97 }}
        aria-label="Send"
        style={{
          flexShrink: 0,
          width: 32, height: 32,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          background: ATELIER.bgInk,
          color: '#fff',
          border: `1px solid ${ATELIER.bgInk}`,
          borderRadius: '50%',
          cursor: 'pointer',
          boxShadow: hover
            ? '0 6px 18px -6px rgba(124,58,237,0.50)'
            : '0 2px 6px rgba(10,10,20,0.18)',
          transition: 'box-shadow 0.18s, background 0.18s',
        }}
      >
        <span style={{ fontSize: 16, fontWeight: 700 }}>↑</span>
      </motion.button>
    );
  }

  // Desktop Send Button (Active)
  return (
    <motion.button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      whileTap={{ scale: 0.97 }}
      aria-label="Send"
      style={{
        flexShrink: 0,
        height: 32,
        padding: '0 4px 0 14px',
        display: 'inline-flex', alignItems: 'center', gap: 8,
        background: ATELIER.bgInk,
        color: '#fff',
        border: `1px solid ${ATELIER.bgInk}`,
        borderRadius: 8,
        cursor: 'pointer',
        fontFamily: FONTS.sans,
        fontSize: 12.5,
        fontWeight: 600,
        letterSpacing: '-0.005em',
        boxShadow: hover
          ? '0 6px 18px -6px rgba(124,58,237,0.50), 0 0 0 1px rgba(167,139,250,0.45) inset, 0 1px 0 rgba(255,255,255,0.10) inset'
          : '0 2px 6px rgba(10,10,20,0.18), 0 1px 0 rgba(255,255,255,0.08) inset',
        transition: 'box-shadow 0.18s, background 0.18s',
      }}
    >
      <span>Send</span>
      <span aria-hidden style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 24, height: 24, borderRadius: 6,
        background: hover ? ATELIER.accent : 'rgba(255,255,255,0.10)',
        fontFamily: FONTS.mono, fontSize: 12, fontWeight: 700,
        color: '#fff',
        transition: 'background 0.18s',
      }}>↵</span>
    </motion.button>
  );
}

/**
 * StopButton - elegant, luxury red-tinted glass action button to cancel/stop an ongoing stream.
 */
function StopButton({ onClick }) {
  const [hover, setHover] = useState(false);

  return (
    <motion.button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      whileTap={{ scale: 0.97 }}
      aria-label="Stop Generation"
      style={{
        flexShrink: 0,
        height: 32,
        padding: '0 12px',
        display: 'inline-flex', alignItems: 'center', gap: 6,
        background: hover ? 'rgba(239, 68, 68, 0.12)' : 'rgba(239, 68, 68, 0.06)',
        color: '#EF4444',
        border: `1px solid ${hover ? 'rgba(239, 68, 68, 0.3)' : 'rgba(239, 68, 68, 0.15)'}`,
        borderRadius: 8,
        cursor: 'pointer',
        fontFamily: FONTS.sans,
        fontSize: 12.5,
        fontWeight: 600,
        letterSpacing: '-0.005em',
        boxShadow: hover ? '0 4px 12px rgba(239, 68, 68, 0.08)' : 'none',
        transition: 'all 0.15s ease',
      }}
    >
      <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor">
        <rect x="4" y="4" width="16" height="16" rx="2" />
      </svg>
      <span>Stop</span>
    </motion.button>
  );
}

function RadioButton({ selected }) {
  return (
    <div style={{
      width: 16,
      height: 16,
      borderRadius: '50%',
      border: `1.5px solid ${selected ? ATELIER.accent : '#cbd5e1'}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.15s ease',
      flexShrink: 0
    }}>
      {selected && (
        <div style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: ATELIER.accent,
        }} />
      )}
    </div>
  );
}

const MULTI_AGENT_ICON_BASE64 = "data:image/png;base64,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";

function MoaIcon() {
  return (
    <div style={{
      width: 26,
      height: 26,
      borderRadius: '50%',
      overflow: 'hidden',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0
    }}>
      <img 
        src={MULTI_AGENT_ICON_BASE64} 
        alt="Multi Agent" 
        style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
      />
    </div>
  );
}

function GlobeIcon() {
  return (
    <div style={{
      width: 26,
      height: 26,
      borderRadius: '50%',
      background: '#f1f5f9',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#475569',
      flexShrink: 0
    }}>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <line x1="2" y1="12" x2="22" y2="12" />
        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
      </svg>
    </div>
  );
}

function ToggleSwitch({ checked, onChange }) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onChange(!checked);
      }}
      style={{
        width: 36,
        height: 20,
        borderRadius: 99,
        background: checked ? ATELIER.accent : '#cbd5e1',
        position: 'relative',
        border: 'none',
        cursor: 'pointer',
        padding: 0,
        display: 'flex',
        alignItems: 'center',
        transition: 'background-color 0.2s ease',
        flexShrink: 0
      }}
    >
      <motion.div
        layout
        transition={{ type: 'spring', stiffness: 500, damping: 30 }}
        style={{
          width: 16,
          height: 16,
          borderRadius: '50%',
          background: '#ffffff',
          boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
          position: 'absolute',
          left: checked ? 18 : 2,
        }}
      />
    </button>
  );
}

export function ModelMenu({ modelList, activeModel, defaultModels, activeMode, onSelect, webSearch, onToggleWebSearch, mobile = false }) {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredModelList = React.useMemo(() => {
    if (!searchQuery.trim()) return modelList;
    const query = searchQuery.toLowerCase();
    return modelList.filter(m => 
      (m.label && m.label.toLowerCase().includes(query)) ||
      (m.provider && m.provider.toLowerCase().includes(query)) ||
      (m.id && m.id.toLowerCase().includes(query))
    );
  }, [modelList, searchQuery]);

  const getActualProvider = React.useCallback((m) => {
    let rawProvider = (m.original_provider || m.provider || 'Other').trim();
    if (rawProvider.toLowerCase() === 'openrouter') {
      const id = m.model_id || m.id || '';
      if (id.includes('/')) {
        rawProvider = id.split('/')[0];
      }
    }
    return rawProvider;
  }, []);

  const flatSortedModels = React.useMemo(() => {
    const groups = {};
    const providerMap = {
      'openai': 'OpenAI',
      'open ai': 'OpenAI',
      'anthropic': 'Anthropic',
      'google': 'Google',
      'meta': 'Meta',
      'meta-llama': 'Meta',
      'x-ai': 'xAI',
      'xai': 'xAI',
      'mistral': 'Mistral',
      'mistralai': 'Mistral',
      'cohere': 'Cohere',
      'deepseek': 'DeepSeek',
      'qwen': 'Qwen',
      'microsoft': 'Microsoft',
      'perplexity': 'Perplexity',
      'ai21': 'AI21',
      'kei.ai': 'DAGGPT Network',
      'kie.ai': 'DAGGPT Network',
      'sao10k': 'Sao10k',
      'z.ai': 'Z.ai',
      'neets': 'Neets',
      'lepton': 'Lepton',
      'novita': 'Novita',
      'together': 'Together',
      'fireworks': 'Fireworks',
      'deepinfra': 'DeepInfra',
      'huggingface': 'HuggingFace',
      'openrouter': 'OpenRouter',
      'unsloth': 'Unsloth',
      'reka': 'Reka',
      'minimax': 'MiniMax',
      'bytedance': 'ByteDance',
      '01-ai': '01-AI',
      'aion-labs': 'Aion Labs',
      'inflection': 'Inflection',
      'writer': 'Writer',
      'upstage': 'Upstage',
      'stepfun': 'Stepfun',
      'stability-ai': 'Stability AI',
      'stability': 'Stability AI',
      'runway': 'Runway'
    };

    filteredModelList.forEach(m => {
      if (m.id === 'openrouter/auto') return;
      const rawProvider = getActualProvider(m);
      const p = providerMap[rawProvider.toLowerCase()] || rawProvider;
      if (!groups[p]) groups[p] = [];
      groups[p].push(m);
    });

    const sortedProviders = Object.keys(groups).sort((a, b) => a.localeCompare(b));

    const flat = [];
    sortedProviders.forEach(p => {
      flat.push(...groups[p]);
    });
    return flat;
  }, [filteredModelList, getActualProvider]);

  const getProviderLogo = (prov) => {
    const logoMap = {
      'Anthropic': '/claude-ai-icon.svg',
      'anthropic': '/claude-ai-icon.svg',
      'OpenAI': '/images/logos/openai.svg',
      'openai': '/images/logos/openai.svg',
      'Google': '/google-icon.svg',
      'google': '/google-icon.svg',
      'Meta': '/meta-icon.svg',
      'meta': '/meta-icon.svg',
      'meta-llama': '/meta-icon.svg',
      'xAI': '/xai-icon.svg',
      'xai': '/xai-icon.svg',
      'x-ai': '/xai-icon.svg',
      'Mistral': '/mistral-icon.svg',
      'mistral': '/mistral-icon.svg',
      'mistralai': '/mistral-icon.svg',
      'Cohere': '/images/logos/cohere.svg',
      'cohere': '/images/logos/cohere.svg',
      'DeepSeek': '/deepseek-logo-icon.svg',
      'deepseek': '/deepseek-logo-icon.svg',
      'Qwen': '/qwen-ai-icon.svg',
      'qwen': '/qwen-ai-icon.svg',
      'Microsoft': '/microsoft-icon.svg',
      'microsoft': '/microsoft-icon.svg',
      'Perplexity': '/images/logos/perplexity.svg',
      'perplexity': '/images/logos/perplexity.svg',
      'AI21': '/ai21-icon.svg',
      'ai21': '/ai21-icon.svg'
    };
    
    const domainMap = {
      'mancer': 'mancer.tech',
      'nousresearch': 'nousresearch.com',
      'nous': 'nousresearch.com',
      'thedrummer': 'huggingface.co',
      'inflection': 'inflection.ai',
      'nvidia': 'nvidia.com',
      'amazon': 'amazon.com',
      'minimax': 'minimaxi.com',
      'aionlabs': 'aionlabs.com',
      'allenai': 'allenai.org',
      'eleutherai': 'eleuther.ai',
      'arcee ai': 'arcee.ai',
      'baidu': 'baidu.com',
      'tencent': 'tencent.com',
      'venice': 'venice.ai',
      'moonshotai': 'moonshot.cn',
      'bytedance': 'bytedance.com',
      'meituan': 'meituan.com',
      'ibm': 'ibm.com',
      'liquidai': 'liquid.ai',
      'essentialai': 'essential.ai',
      'xiaomi': 'xiaomi.com',
      'writer': 'writer.com',
      'upstage': 'upstage.ai',
      'stepfun': 'stepfun.com',
      'stability': 'stability.ai',
      'runway': 'runwayml.com',
      'suno': 'suno.com',
      'kuaishou': 'kuaishou.com',
      'ideogram': 'ideogram.ai',
      '01.ai': '01.ai'
    };
    
    const p = prov.toLowerCase();
    const mappedProv = Object.keys(logoMap).find(k => k.toLowerCase() === p) || prov;
    const logoSrc = logoMap[mappedProv];

    if (logoSrc) {
      return (
        <img 
          src={logoSrc} 
          alt={prov} 
          style={{ width: 14, height: 14, borderRadius: 2, objectFit: 'contain' }} 
        />
      );
    }
    
    const domain = domainMap[p] || domainMap[p.replace(/[^a-z0-9]/g, '')];
    if (domain) {
      return (
        <img 
          src={`https://www.google.com/s2/favicons?domain=${domain}&sz=32`} 
          alt={prov} 
          style={{ width: 14, height: 14, borderRadius: 2, objectFit: 'contain' }} 
        />
      );
    }

    return <span style={{ fontSize: 13, lineHeight: 1 }}>⚡</span>;
  };

  return (
    <div style={{
      background: mobile ? 'transparent' : ATELIER.bg,
      border: mobile ? 'none' : `1px solid ${ATELIER.borderStrong}`,
      borderRadius: mobile ? 0 : 16,
      width: mobile ? '100%' : 290,
      boxShadow: mobile ? 'none' : ATELIER.shadowL,
      display: 'flex', flexDirection: 'column',
      overflow: 'hidden',
      flex: mobile ? 1 : 'none',
      minHeight: mobile ? 0 : 'auto',
    }}>
      {/* Search Input Bar */}
      <div style={{ padding: mobile ? '12px 8px 8px' : '12px 14px 8px', borderBottom: mobile ? 'none' : `1px solid ${ATELIER.borderSoft}` }}>
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
            stroke={ATELIER.inkSoft} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
            style={{ position: 'absolute', left: 10, pointerEvents: 'none', opacity: 0.7 }}
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search models..."
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%',
              padding: '6px 28px 6px 30px',
              fontSize: 12,
              background: ATELIER.bgHover,
              border: `1px solid ${ATELIER.border}`,
              borderRadius: 8,
              outline: 'none',
              fontFamily: FONTS.sans,
              color: ATELIER.ink,
              transition: 'border-color 0.15s, background 0.15s',
            }}
            onFocus={(e) => {
              e.target.style.borderColor = ATELIER.accent;
              e.target.style.background = '#fff';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = ATELIER.border;
              e.target.style.background = ATELIER.bgHover;
            }}
          />
          {searchQuery && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setSearchQuery('');
              }}
              style={{
                position: 'absolute', right: 8,
                background: 'transparent', border: 'none', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: 4, borderRadius: '50%',
                color: ATELIER.inkSoft,
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = ATELIER.bgHover}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Models List Scroll Container */}
      <div className="dgw-scroll-hide" style={{ flex: mobile ? 1 : 'none', maxHeight: mobile ? 'none' : 380, overflowY: 'auto', minHeight: 0 }}>
        {/* Pinned top: Mixture of Agents (Only in Chat mode) */}
        {activeMode === 'chat' && !searchQuery.trim() && (
          <div
            onClick={() => onSelect('openrouter/auto')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: mobile ? '12px 12px' : '12px 14px',
              borderRadius: mobile ? 12 : 0,
              cursor: 'pointer',
              background: activeModel?.id === 'openrouter/auto' ? 'rgba(124, 58, 237, 0.05)' : 'transparent',
              borderBottom: mobile ? 'none' : `1px solid ${ATELIER.borderSoft}`,
              transition: 'background 0.15s ease'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = activeModel?.id === 'openrouter/auto' ? 'rgba(124, 58, 237, 0.08)' : ATELIER.bgHover}
            onMouseLeave={(e) => e.currentTarget.style.background = activeModel?.id === 'openrouter/auto' ? 'rgba(124, 58, 237, 0.05)' : 'transparent'}
          >
            <MoaIcon />
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <span style={{ fontFamily: FONTS.sans, fontSize: 13, fontWeight: 600, color: ATELIER.ink }}>
                Multi Agent
              </span>
              <span style={{ fontFamily: FONTS.sans, fontSize: 11, color: ATELIER.inkSoft, marginTop: 1, textAlign: 'left' }}>
                Auto-mixes best AI models for your task.
              </span>
            </div>
            <RadioButton selected={activeModel?.id === 'openrouter/auto'} />
          </div>
        )}

        {/* Flat models list grouped alphabetically by provider */}
        {flatSortedModels.map(m => (
          <div
            key={m.id}
            onClick={() => onSelect(m.id)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: mobile ? '10px 12px' : '10px 14px',
              borderRadius: mobile ? 12 : 0,
              cursor: 'pointer',
              background: activeModel?.id === m.id ? 'rgba(124, 58, 237, 0.05)' : 'transparent',
              transition: 'background 0.15s ease'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = activeModel?.id === m.id ? 'rgba(124, 58, 237, 0.08)' : ATELIER.bgHover}
            onMouseLeave={(e) => e.currentTarget.style.background = activeModel?.id === m.id ? 'rgba(124, 58, 237, 0.05)' : 'transparent'}
          >
            <div style={{ width: 26, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              {getProviderLogo(getActualProvider(m))}
            </div>
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ 
                fontFamily: FONTS.sans, 
                fontSize: 13, 
                fontWeight: activeModel?.id === m.id ? 600 : 500, 
                color: activeModel?.id === m.id ? ATELIER.accent : ATELIER.ink 
              }}>
                {m.label}
              </span>
              {m.badge && (
                <span style={{
                  fontFamily: FONTS.mono,
                  fontSize: 8.5,
                  fontWeight: 700,
                  padding: '2px 5px',
                  borderRadius: 4,
                  background: activeModel?.id === m.id ? 'rgba(124,58,237,0.1)' : '#f1f5f9',
                  color: activeModel?.id === m.id ? ATELIER.accent : '#64748b',
                  letterSpacing: '0.05em',
                  marginLeft: 8
                }}>
                  {m.badge}
                </span>
              )}
            </div>
            <RadioButton selected={activeModel?.id === m.id} />
          </div>
        ))}

        {/* Empty state fallback when no models match search */}
        {flatSortedModels.length === 0 && (activeMode !== 'chat' || searchQuery.trim()) && (
          <div style={{ padding: '24px 14px', textAlign: 'center', color: ATELIER.inkSoft }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
              style={{ margin: '0 auto 8px', opacity: 0.4, display: 'block' }}
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <div style={{ fontSize: 12, fontWeight: 500 }}>No models match your search</div>
          </div>
        )}
      </div>

      {/* Web Search Footer (Only in Chat mode) */}
      {activeMode === 'chat' && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: mobile ? '12px 12px' : '12px 14px',
          borderRadius: mobile ? 12 : 0,
          background: mobile ? 'transparent' : '#f8fafc',
          borderTop: mobile ? 'none' : `1px solid ${ATELIER.borderSoft}`,
        }}>
          <GlobeIcon />
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <span style={{ fontFamily: FONTS.sans, fontSize: 13, fontWeight: 600, color: '#334155', textAlign: 'left' }}>
              Search Web
            </span>
            <span style={{ fontFamily: FONTS.sans, fontSize: 11, color: '#64748b', marginTop: 1, textAlign: 'left' }}>
              Retrieve from Internet
            </span>
          </div>
          <ToggleSwitch checked={webSearch} onChange={onToggleWebSearch} />
        </div>
      )}
    </div>
  );
}

